<script lang="ts">
  import type { S3Execution } from '../../services/s3-client';

  interface Props {
    executions: S3Execution[];
    selectedId: string | null;
    onselect: (id: string) => void;
    onsearch?: (id: string) => void;
    loading: boolean;
  }

  let { executions, selectedId, onselect, onsearch, loading }: Props = $props();

  let search = $state('');
  let searching = $state(false);

  function looksLikeId(q: string): boolean {
    return q.includes('-') || q.length > 8;
  }

  async function handleSearch() {
    const q = search.trim();
    if (!q || !looksLikeId(q) || !onsearch) return;
    searching = true;
    try {
      onsearch(q);
    } finally {
      searching = false;
    }
  }

  function handleKeydown(e: KeyboardEvent) {
    if (e.key === 'Enter') handleSearch();
  }

  const filtered = $derived(
    search
      ? executions.filter(e => e.id.toLowerCase().includes(search.toLowerCase()))
      : executions
  );

  function relativeTime(iso: string): string {
    const diff = Date.now() - new Date(iso).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  }

  function formatDuration(ms?: number): string {
    if (!ms) return '—';
    const secs = Math.floor(ms / 1000);
    if (secs < 60) return `${secs}s`;
    return `${Math.floor(secs / 60)}m ${secs % 60}s`;
  }
</script>

<div class="flex flex-col h-full">
  <!-- Search -->
  <div class="p-3 border-b border-border">
    <div class="flex gap-1.5">
      <div class="relative flex-1">
        <svg class="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <circle cx="11" cy="11" r="8" /><path d="m21 21-4.35-4.35" />
        </svg>
        <input
          type="text"
          bind:value={search}
          onkeydown={handleKeydown}
          placeholder="Filter or search execution ID..."
          class="w-full pl-8 pr-3 py-1.5 text-xs bg-bg-input border border-border rounded-lg text-text-primary placeholder:text-text-muted focus:outline-none focus:border-accent transition-colors"
        />
      </div>
      {#if search.trim() && looksLikeId(search.trim())}
        <button
          onclick={handleSearch}
          disabled={searching}
          class="px-2.5 py-1.5 text-xs bg-accent hover:bg-accent-hover text-white rounded-lg font-medium transition-colors disabled:opacity-50 shrink-0"
          title="Search S3 for this execution ID"
        >
          {searching ? '…' : 'Go'}
        </button>
      {/if}
    </div>
  </div>

  <!-- List -->
  <div class="flex-1 overflow-y-auto">
    {#if loading}
      {#each Array(6) as _}
        <div class="p-3 border-b border-border-light">
          <div class="h-3 w-3/4 bg-bg-card rounded animate-skeleton-pulse mb-2"></div>
          <div class="h-2.5 w-1/2 bg-bg-card rounded animate-skeleton-pulse"></div>
        </div>
      {/each}
    {:else if filtered.length === 0}
      <div class="p-6 text-center text-text-muted text-xs">
        {search ? 'No executions match filter' : 'No executions found'}
      </div>
    {:else}
      {#each filtered as exec (exec.id)}
        <button
          onclick={() => onselect(exec.id)}
          class="w-full text-left p-3 border-b border-border-light transition-colors hover:bg-bg-hover
            {selectedId === exec.id ? 'bg-accent/10 border-l-2 border-l-accent' : 'border-l-2 border-l-transparent'}"
        >
          <div class="flex items-center gap-2 mb-1">
            <span class="w-2 h-2 rounded-full shrink-0 {exec.status === 'completed' ? 'bg-success' : 'bg-error'}"></span>
            <span class="text-xs font-mono text-text-primary break-all">{exec.id}</span>
          </div>
          <div class="flex items-center gap-3 text-[10px] text-text-muted ml-4">
            <span>{relativeTime(exec.lastModified)}</span>
            <span>{formatDuration(exec.duration)}</span>
            <span>{exec.nodeCount} files</span>
          </div>
        </button>
      {/each}
    {/if}
  </div>
</div>
